import {RefObject, useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BellIcon, FiltersIcon, MapIcon, SearchIcon, SortIcon, TilesIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";
import {useScrolledFromTop} from "@pg-mono/hooks";
import {FILTERS_TEST_IDS, NAVIGATION_MOBILE_TEST_IDS, NAVIGATION_TEST_IDS, OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../app/rp_reducer";
import {OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {generatePillLabel} from "../utils/generate_pill_label";
import {ISearchInputValue} from "./ISearchInputValue";

interface IProps {
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    listUrl?: string;
    mapUrl?: string;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: RefObject<HTMLDivElement | null>) => void;
    firstSelectedRegion?: string;
    isMap?: boolean;
    sortActive?: boolean;
    isAbroad?: boolean;
    disableAlert?: boolean;
    showLocationSearch?: boolean;
    searchFieldPropsValue?: ISearchInputValue;
    forceShowTopSearchTrigger?: boolean;
}

const mobileSearchTriggersWrapperHeight = 64;
const scrollThreshold = 100;

export const MobileTopSearchTriggers = (props: IProps) => {
    const {
        latestQuery,
        onToggleFiltersClick,
        onToggleSortClick,
        selectedFiltersCount,
        disableAlert,
        firstSelectedRegion,
        isMap,
        listUrl,
        mapUrl,
        forceShowTopSearchTrigger,
        isAbroad,
        onMount,
        onToggleSearchClick,
        searchFieldPropsValue,
        showLocationSearch,
        sortActive
    } = props;

    const dispatch = useDispatch();

    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const regularOffersCount = useSelector((state: IRPStore) => state.offerList.list.count);
    const investmentOffersCount = useSelector((state: IRPStore) => state.investmentOffer.list.offers.count);

    const isInvestmentView = viewType === ViewType.FOR_INVESTMENT_LIST || viewType === ViewType.FOR_INVESTMENT_LIST_MAP;

    const offersCount = isInvestmentView ? investmentOffersCount : regularOffersCount;

    const searchTriggerRef = useRef<HTMLDivElement>(null);

    const hasScrolledPastThreshold = useScrolledFromTop(scrollThreshold);

    useEffect(() => {
        onMount && onMount(searchTriggerRef);
    }, []);

    const onSortClick = () => {
        onToggleSortClick && onToggleSortClick();
    };

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    const regionPillLabel = useMemo(() => {
        if (!searchFieldPropsValue) {
            return firstSelectedRegion || null;
        }

        return generatePillLabel(false, searchFieldPropsValue);
    }, [searchFieldPropsValue]);

    return (
        <div className={wrapper}>
            <div className={triggersHolder}>
                {!isAbroad && showLocationSearch && (
                    <div className={inputWrapper} onClick={onToggleSearchClick}>
                        <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                        {firstSelectedRegion ? (
                            <Pill size="small">
                                <Text noWrap className={inputWrapperText}>
                                    {regionPillLabel}
                                </Text>
                            </Pill>
                        ) : (
                            <Text noWrap className={inputWrapperText} dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                Dodaj lokalizację
                            </Text>
                        )}
                    </div>
                )}

                <div className={triggerIconHolder} onClick={onToggleFiltersClick} data-testid={FILTERS_TEST_IDS.FILTER_MOBILE_ICON}>
                    <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                    <Text variant="info_txt_2" className={gapTop}>
                        filtruj
                    </Text>

                    {selectedFiltersCount ? <div className={triggerBadge}>{selectedFiltersCount}</div> : ""}
                </div>

                <div className={triggerIconHolder} onClick={onSortClick} data-testid={NAVIGATION_TEST_IDS.SORT_ICON}>
                    <SortIcon
                        size="2.4"
                        wrapperSize="2.4"
                        wrapperColor="transparent"
                        fill={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}
                    />

                    <Text variant="info_txt_2" className={gapTop} color={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}>
                        sortuj
                    </Text>
                </div>

                {latestQuery?.region && !isAbroad && !disableAlert && (
                    <OfferListNotificationsSubscriptionModal
                        customButton={(props) => (
                            <div className={triggerIconHolder} {...props}>
                                <BellIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                                <Text variant="info_txt_2" className={gapTop}>
                                    alerty
                                </Text>
                            </div>
                        )}
                    />
                )}
            </div>

            {(offersCount > 0 || forceShowTopSearchTrigger) && (hasScrolledPastThreshold || isMap) && (
                <div className={mapButtons}>
                    <Button
                        size="x-small"
                        variant="filled_secondary"
                        href={isMap ? listUrl : mapUrl}
                        className={mapButton}
                        iconLeft={isMap ? TilesIcon : MapIcon}
                        dataTestId={OFFER_TEST_IDS.LISTING_OFFER_BOX.TOGGLE_MAP_BUTTON}
                    >
                        <Text variant="button_small">{isMap ? "Lista" : "Mapa"}</Text>
                    </Button>
                </div>
            )}
        </div>
    );
};

const wrapper = css({
    background: "var(--colors-white)",

    md: {
        display: "none"
    }
});

const triggersHolder = css({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "0 auto",
    gap: 3,
    width: "100%"
});

const triggerIconHolder = css({
    color: "var(--colors-secondary)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",

    "&:visited, &:hover, &:focus, &:active": {
        color: "var(--colors-secondary)",
        textDecoration: "none"
    }
});

const triggerBadge = css({
    position: "absolute",
    top: 0,
    right: 0,
    color: "var(--colors-white)",
    background: "var(--colors-danger)",
    fontSize: "var(--globals-text-root)",
    borderRadius: "50%",
    border: "1px solid var(--colors-white)",
    width: "1.6rem",
    height: "1.6rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transform: "translateX(50%)"
});

const mapButtons = css({
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: 1.5,
    display: "flex",
    columnGap: 3,
    zIndex: 10
});

const mapButton = css({
    boxShadow: "var(--shadow-4)",
    gap: 1,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    width: "106px",
    zIndex: 10
});

const inputWrapper = css({
    display: "flex",
    justifyContent: "normal",
    alignItems: "center",
    boxShadow: "var(--shadow-2)",
    border: "1px solid var(--colors-gray-600)",
    width: "100%",
    mr: -1,
    p: 1,
    px: 2,
    gap: 1,
    borderRadius: 1
});

const inputWrapperText = css({
    pr: 1,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "calc(100vw - 261px)"
});

const gapTop = css({
    mt: 0.5
});
import {RefObject, useEffect, useMemo, useRef} from "react";
import {useDispatch, useSelector} from "react-redux";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {BellIcon, FiltersIcon, MapIcon, SearchIcon, SortIcon, TilesIcon} from "@pg-design/icons-module";
import {Pill} from "@pg-design/pill-module";
import {Text} from "@pg-design/text-module";
import {useScrolledFromTop} from "@pg-mono/hooks";
import {FILTERS_TEST_IDS, NAVIGATION_MOBILE_TEST_IDS, NAVIGATION_TEST_IDS, OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {IRPStore} from "../../app/rp_reducer";
import {OfferListNotificationsSubscriptionModal} from "../../offer/list/components/OfferListNotificationsSubscriptionModal";
import {setBottomFixedElementHeight} from "../../ui/actions/ui_actions";
import {ILatestQuery} from "../../utils/latest_query";
import {ViewType} from "../../view_type/ViewType";
import {generatePillLabel} from "../utils/generate_pill_label";
import {ISearchInputValue} from "./ISearchInputValue";

interface IProps {
    selectedFiltersCount: number;
    onToggleSearchClick?: () => void;
    onToggleFiltersClick: () => void;
    onToggleSortClick: (() => void) | null;
    listUrl?: string;
    mapUrl?: string;
    latestQuery: ILatestQuery;
    onMount?: (searchTriggerRef: RefObject<HTMLDivElement | null>) => void;
    firstSelectedRegion?: string;
    isMap?: boolean;
    sortActive?: boolean;
    isAbroad?: boolean;
    disableAlert?: boolean;
    showLocationSearch?: boolean;
    searchFieldPropsValue?: ISearchInputValue;
    forceShowTopSearchTrigger?: boolean;
}

const mobileSearchTriggersWrapperHeight = 64;
const scrollThreshold = 100;

export const MobileTopSearchTriggers = (props: IProps) => {
    const {
        latestQuery,
        onToggleFiltersClick,
        onToggleSortClick,
        selectedFiltersCount,
        disableAlert,
        firstSelectedRegion,
        isMap,
        listUrl,
        mapUrl,
        forceShowTopSearchTrigger,
        isAbroad,
        onMount,
        onToggleSearchClick,
        searchFieldPropsValue,
        showLocationSearch,
        sortActive
    } = props;

    const dispatch = useDispatch();

    const viewType = useSelector((state: IRPStore) => state.viewType.current);
    const regularOffersCount = useSelector((state: IRPStore) => state.offerList.list.count);
    const investmentOffersCount = useSelector((state: IRPStore) => state.investmentOffer.list.offers.count);

    const isInvestmentView = viewType === ViewType.FOR_INVESTMENT_LIST || viewType === ViewType.FOR_INVESTMENT_LIST_MAP;

    const offersCount = isInvestmentView ? investmentOffersCount : regularOffersCount;

    const searchTriggerRef = useRef<HTMLDivElement>(null);

    const hasScrolledPastThreshold = useScrolledFromTop(scrollThreshold);

    useEffect(() => {
        onMount && onMount(searchTriggerRef);
    }, []);

    const onSortClick = () => {
        onToggleSortClick && onToggleSortClick();
    };

    useEffect(() => {
        dispatch(setBottomFixedElementHeight(mobileSearchTriggersWrapperHeight));
    }, []);

    const regionPillLabel = useMemo(() => {
        if (!searchFieldPropsValue) {
            return firstSelectedRegion || null;
        }

        return generatePillLabel(false, searchFieldPropsValue);
    }, [searchFieldPropsValue]);

    return (
        <div className={wrapper}>
            <div className={triggersHolder}>
                {!isAbroad && showLocationSearch && (
                    <div className={inputWrapper} onClick={onToggleSearchClick}>
                        <SearchIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                        {firstSelectedRegion ? (
                            <Pill size="small">
                                <Text noWrap className={inputWrapperText}>
                                    {regionPillLabel}
                                </Text>
                            </Pill>
                        ) : (
                            <Text noWrap className={inputWrapperText} dataTestId={NAVIGATION_MOBILE_TEST_IDS.SEARCH_BAR.INPUT}>
                                Dodaj lokalizację
                            </Text>
                        )}
                    </div>
                )}

                <div className={triggerIconHolder} onClick={onToggleFiltersClick} data-testid={FILTERS_TEST_IDS.FILTER_MOBILE_ICON}>
                    <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                    <Text variant="info_txt_2" className={gapTop}>
                        filtruj
                    </Text>

                    {selectedFiltersCount ? <div className={triggerBadge}>{selectedFiltersCount}</div> : ""}
                </div>

                <div className={triggerIconHolder} onClick={onSortClick} data-testid={NAVIGATION_TEST_IDS.SORT_ICON}>
                    <SortIcon
                        size="2.4"
                        wrapperSize="2.4"
                        wrapperColor="transparent"
                        fill={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}
                    />

                    <Text variant="info_txt_2" className={gapTop} color={sortActive ? "var(--colors-secondary)" : "var(--colors-gray-600)"}>
                        sortuj
                    </Text>
                </div>

                {latestQuery?.region && !isAbroad && !disableAlert && (
                    <OfferListNotificationsSubscriptionModal
                        customButton={(props) => (
                            <div className={triggerIconHolder} {...props}>
                                <BellIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" />

                                <Text variant="info_txt_2" className={gapTop}>
                                    alerty
                                </Text>
                            </div>
                        )}
                    />
                )}
            </div>

            {(offersCount > 0 || forceShowTopSearchTrigger) && (hasScrolledPastThreshold || isMap) && (
                <div className={mapButtons}>
                    <Button
                        size="x-small"
                        variant="filled_secondary"
                        href={isMap ? listUrl : mapUrl}
                        className={mapButton}
                        iconLeft={isMap ? TilesIcon : MapIcon}
                        dataTestId={OFFER_TEST_IDS.LISTING_OFFER_BOX.TOGGLE_MAP_BUTTON}
                    >
                        <Text variant="button_small">{isMap ? "Lista" : "Mapa"}</Text>
                    </Button>
                </div>
            )}
        </div>
    );
};

const wrapper = css({
    background: "var(--colors-white)",

    md: {
        display: "none"
    }
});

const triggersHolder = css({
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    margin: "0 auto",
    gap: 3,
    width: "100%"
});

const triggerIconHolder = css({
    color: "var(--colors-secondary)",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "default",

    "&:visited, &:hover, &:focus, &:active": {
        color: "var(--colors-secondary)",
        textDecoration: "none"
    }
});

const triggerBadge = css({
    position: "absolute",
    top: 0,
    right: 0,
    color: "var(--colors-white)",
    background: "var(--colors-danger)",
    fontSize: "var(--globals-text-root)",
    borderRadius: "50%",
    border: "1px solid var(--colors-white)",
    width: "1.6rem",
    height: "1.6rem",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    transform: "translateX(50%)"
});

const mapButtons = css({
    position: "fixed",
    left: "50%",
    transform: "translateX(-50%)",
    bottom: 1.5,
    display: "flex",
    columnGap: 3,
    zIndex: 10
});

const mapButton = css({
    boxShadow: "var(--shadow-4)",
    gap: 1,
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    width: "106px",
    zIndex: 10
});

const inputWrapper = css({
    display: "flex",
    justifyContent: "normal",
    alignItems: "center",
    boxShadow: "var(--shadow-2)",
    border: "1px solid var(--colors-gray-600)",
    width: "100%",
    mr: -1,
    p: 1,
    px: 2,
    gap: 1,
    borderRadius: 1
});

const inputWrapperText = css({
    pr: 1,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "calc(100vw - 261px)"
});

const gapTop = css({
    mt: 0.5
});
